import { mapState } from 'vuex';
import castArray from 'lodash-es/castArray';
import map from 'lodash-es/map';
import { resourceCustomReport } from '../../api/resources';
import { REPORT_TYPES } from '../../constants';
import { sortLocations, formatDateString } from './helpers';

export default {
    computed: {
        ...mapState({ listCallTypes: 'callTypes' }),
    },

    methods: {
        formatDateString,
        /**
         * Checks whether or not data array contains all element of allOptions array
         * @param {Array} allOptions all possible options for check on completeness in [{value: "value", name: "name"}] format
         * @param {Array} data array is checked on completeness
         * @return {Boolean}
         */
        isAllDataInArray(allOptions, data) {
            let isAllData = true;
            if (allOptions.length === data.length) {
                allOptions.forEach(opt => {
                    isAllData = isAllData && data.some(item => item === opt.value || item === opt.name);
                });
                return isAllData;
            }

            return false;
        },

        /**
         * Convert array of id's or number to objects with call type names.
         * @param {number|array} callTypes
         * @return {object}
         */
        parseCallTypes(callTypes) {
            let mappedCallTypes = [];
            (Array.isArray(callTypes) ? callTypes.map(c => c) : [callTypes]).forEach(call =>
                this.listCallTypes.some(ct => ct.id === Number(call) && mappedCallTypes.push(ct))
            );

            return mappedCallTypes.map(c => c.name);
        },

        /**
         * Returns true if colored icon should be rendered
         * @param {string} event
         * @returns {boolean}
         */
        showColoredIcon(event) {
            let i = event.split('.')[0];
            return (
                i === 'message' ||
                i === 'accept' ||
                i === 'reject' ||
                i === 'remote_cancel' ||
                i === 'speech' ||
                i === 'speech_disconnect' ||
                i === 'user_in' ||
                i === 'user_out'
            );
        },

        /**
         * Returns name of svg icon or class name for icon in font
         * @param {string} event
         * @returns {string}
         */
        iconClass(event) {
            return event.split('.')[0];
        },
    },
};

/**
 * Sends post save custom report api call
 * @param id
 * @param reportData
 * @return {Promise}
 */
export function saveReport(id, reportData) {
    return resourceCustomReport({
        method: 'post',
        id: id ? id : null,
        data: reportData,
    });
}

/**
 * Returns report object appropriate for saving
 * @param reportData
 * @param aAreas
 * @param callTypes
 * @returns {Object}
 */
export function getCustomReportObject(reportData, aAreas, callTypes) {
    let report = {
        name: reportData.reportName,
        type: reportData.type,
        configuration: {
            aa: getSelectedAANames(reportData, aAreas),
            locations: getSelectedLocationNames(reportData, aAreas),
            call_types: getSelectedCallTypeIDs(reportData, callTypes),
            startDate: reportData.startDate,
            endDate: reportData.endDate,
        },
        schedule_emails: reportData.scheduleEmails,
        schedule_type: reportData.scheduleType,
        emailing_time: reportData.emailingTime,
        schedule_description: reportData.scheduleDescription,
        schedule_last_run: reportData.scheduleLastRun,
    };

    let additionalConfiguration = {};
    switch (reportData.type) {
        case REPORT_TYPES.ASCOM_EVENT:
            additionalConfiguration = {
                selectedEvent: {
                    calltrace_id: reportData.calltrace_id,
                    types: reportData.event_types,
                    location: reportData.event_location,
                    start: reportData.start_time,
                    end: reportData.end_time,
                    incomplete: reportData.incomplete,
                },
            };
            break;
        case REPORT_TYPES.PAIR_EVENT:
            additionalConfiguration = {
                pair_type: reportData.pairEvent,
            };
            break;
        case REPORT_TYPES.EXCEPTION:
            additionalConfiguration = {
                call_type: reportData.callType,
            };
            break;
        case REPORT_TYPES.STAFF_PERFORMANCE:
            additionalConfiguration = {
                call_id: reportData.call_id,
                call_id_names: reportData.call_id_names,
            };
            break;
    }
    Object.assign(report.configuration, additionalConfiguration);

    return report;
}

/**
 * Returns selected call type for exception report
 * @param reportData
 * @param callTypes
 * @return {Object}
 */
export function getSelectedCallType(reportData, callTypes) {
    return reportData.type === REPORT_TYPES.EXCEPTION
        ? callTypes.find(e => e.id === Number(reportData.callType))
        : {};
}

/**
 * Returns selected call types or all call types
 * @param reportData
 * @param callTypes
 * @return {Object[]} array of call type objects
 */
export function getSelectedCallTypes(reportData, callTypes) {
    let reportDataCallTypeNames = reportData.ct //  hack to unify function for showReportForm and ReportForm
        ? reportData.ct //  TODO Remove this hack after report structure unification (https://jira.ascom-ws.com/browse/MERA-1540)
        : reportData.callTypes.map(ct => String(ct.value));
    return (reportDataCallTypeNames || []).length === 0
        ? callTypes
        : callTypes.filter(callType => reportDataCallTypeNames.some(c => c === callType.id));
}

/**
 * Returns selected assignment areas or all assignment areas
 * @param reportData
 * @param aAreas
 * @return {Object[]} array of assignment area objects
 */
export function getSelectedAAreas(reportData, aAreas) {
    return (reportData.aa || []).length === 0
        ? aAreas
        : aAreas.filter(
              //   hack to unify function for showReportForm and ReportForm
              area => reportData.aa.includes(area.name) || reportData.aa.some(aa => aa.value === area.id) //  TODO Remove this hack after report structure unification (https://jira.ascom-ws.com/browse/MERA-1540)
          );
}

/**
 * Returns selected locations or all locations
 * @param reportData
 * @param aAreas
 * @return {Object[]} array of location objects
 */
export function getSelectedLocations(reportData, aAreas) {
    let locations = [];
    let reportDataLocationNames = reportData.loc //   hack to unify function for showReportForm and ReportForm
        ? reportData.loc
        : reportData.locations.map(l => l.name); //  TODO Remove this hack after report structure unification (https://jira.ascom-ws.com/browse/MERA-1540)
    getSelectedAAreas(reportData, aAreas).forEach(aArea => {
        locations = locations.concat(
            aArea.locations.filter(location =>
                (reportDataLocationNames || []).length === 0
                    ? true
                    : reportDataLocationNames.includes(location.name)
            )
        );
    });
    return locations;
}

/**
 * Returns names of selected assignment areas
 * @param reportData
 * @param aAreas
 * @return {String[]} array of assignment area names
 */
export function getSelectedAANames(reportData, aAreas) {
    return sortLocations(map(getSelectedAAreas(reportData, aAreas), 'name'));
}

/**
 * Returns names of selected locations
 * @param reportData
 * @param aAreas
 * @return {String[]} array of location names
 */
export function getSelectedLocationNames(reportData, aAreas) {
    return sortLocations(map(getSelectedLocations(reportData, aAreas), 'name'));
}

/**
 * Returns names of selected call types
 * @param reportData
 * @param callTypes
 * @return {String[]} array of call type names
 */
export function getSelectedCallTypeNames(reportData, callTypes) {
    return map(getSelectedCallTypes(reportData, callTypes), 'name');
}

/**
 * Returns IDs of selected call types
 * @param reportData
 * @param callTypes
 * @return {Int[]} array of call types IDs
 */
export function getSelectedCallTypeIDs(reportData, callTypes) {
    return map(getSelectedCallTypes(reportData, callTypes), 'id');
}

/**
 * Convert string or array to plain array
 * @param {string|array} prop
 * @return {String[]}
 */
export function toArray(prop) {
    return prop ? castArray(prop).map(i => String(i)) : [];
}

/**
 * Convert number or array to plain array
 * @param {number|array} prop
 * @return {Number[]}
 */
export function toNumberArray(prop) {
    return prop
        ? castArray(prop)
              .map(Number)
              .filter(Number.isInteger)
        : [];
}
